import { Component, computed, ElementRef, inject } from '@angular/core';
import { getAuthenticatedDepartmentTreeOptions } from '@app/reducers/orm/department/department.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { getPermissionState } from '@reducers/auth/permission.helper';
import { AppState } from '@reducers/interfaces';
import type { EnhancedAbsenceModel } from '@reducers/orm/absence/absence.model';
import { getAbsenteeOptionEntities } from '@reducers/orm/absentee-option/absentee-option.service';
import {
  getEmployeeEntities,
  getEmployeeTeamDepartments,
  getEmployeeTeamDepartmentsWithoutFlexpool,
} from '@reducers/orm/employee/employee.selectors';
import { type ExchangeWithScheduleModel } from '@reducers/orm/exchange/exchange.model';
import { selectSelectedDepartmentIds } from '@reducers/selected-departments/selected-departments.service';
import { mgcIconClipboardRegular } from '@sb/mgc-icons/mgcIcon-clipboard_regular.icon';
import { mgcIconWarningRegular } from '@sb/mgc-icons/mgcIcon-warning_regular.icon';
import { generateId } from '@sb/shared/util';
import { ButtonComponent } from '@sb/ui/button';
import { EmptyStateComponent, LoadingStateComponent } from '@sb/ui/feedback';
import { IconRegistry } from '@sb/ui/icon';
import { SubscriptionPlanDirective } from '@shared/subscription-plan/subscription-plan.directive';
import { PlanType } from '@shiftbase-com/ts-shared/models';

import { DepartmentsDropdownComponent } from '../../departments-dropdown/departments-dropdown.component';
import { AbsenceRequestNotificationsComponent } from '../absence-request-notifications.component';
import { ExchangeRequestContext, ExchangeRequestsComponent } from '../exchange-requests.component';
import {
  countSectionItems,
  toAbsenceDepartmentSections,
  toDepartmentOptions,
  toPendingDepartmentSections,
} from '../requests/department-sections.helper';
import {
  filterEmployeeApprovalExchanges,
  filterSupervisorApprovalExchanges,
  toApprovableAbsences,
  toApprovableDepartmentsByEmployee,
  toVisibleExchanges,
  useExchangeViewContext,
} from '../requests/request-rows.helper';
import { DepartmentSectionsComponent } from './department-sections.component';
import { RequestsPanelStore } from './requests-panel.store';

type PendingItem = EnhancedAbsenceModel | ExchangeWithScheduleModel;

interface PendingSplit {
  absences: EnhancedAbsenceModel[];
  supervisorApproval: ExchangeWithScheduleModel[];
  employeeAcceptance: ExchangeWithScheduleModel[];
}

@Component({
  selector: 'supervisor-requests-content',
  templateUrl: './supervisor-requests-content.component.html',
  host: { tabindex: '-1' },
  imports: [
    TranslatePipe,
    SubscriptionPlanDirective,
    ButtonComponent,
    EmptyStateComponent,
    LoadingStateComponent,
    AbsenceRequestNotificationsComponent,
    DepartmentSectionsComponent,
    DepartmentsDropdownComponent,
    ExchangeRequestsComponent,
  ],
  providers: [RequestsPanelStore],
})
export class SupervisorRequestsContentComponent {
  #store = inject<Store<AppState>>(Store);
  #elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  #panel = inject(RequestsPanelStore);

  #permissionState = this.#store.selectSignal(getPermissionState);
  #employees = this.#store.selectSignal(getEmployeeEntities);
  #absenteeOptions = this.#store.selectSignal(getAbsenteeOptionEntities);
  #employeeTeamDepartments = this.#store.selectSignal(getEmployeeTeamDepartments);
  #approvableTeamDepartments = this.#store.selectSignal(getEmployeeTeamDepartmentsWithoutFlexpool);
  #selectedDepartmentIds = this.#store.selectSignal(selectSelectedDepartmentIds);
  #departmentTree = this.#store.selectSignal(getAuthenticatedDepartmentTreeOptions);

  idPrefix = generateId('supervisor-requests');

  loading = this.#panel.isLoading;
  isError = this.#panel.isError;

  #exchangeViewContext = useExchangeViewContext();

  #visibleExchanges = computed(() => toVisibleExchanges(this.#panel.exchangeRows(), this.#exchangeViewContext()));

  #absenceViewContext = computed(() => ({
    employees: this.#employees(),
    absenteeOptions: this.#absenteeOptions(),
    employeeTeamDepartments: this.#employeeTeamDepartments(),
    approvableTeamDepartments: this.#approvableTeamDepartments(),
    permissionState: this.#permissionState(),
  }));

  supervisorApprovalExchanges = computed(() =>
    filterSupervisorApprovalExchanges(this.#visibleExchanges(), this.#permissionState()),
  );
  employeeApprovalExchanges = computed(() => filterEmployeeApprovalExchanges(this.#visibleExchanges()));
  pendingAbsenceRequests = computed(() =>
    toApprovableAbsences(this.#panel.pendingAbsenceRows(), this.#absenceViewContext()),
  );
  approvedOpenEndedAbsences = computed(() =>
    toApprovableAbsences(this.#panel.openEndedAbsenceRows(), this.#absenceViewContext()),
  );

  #departmentOptions = computed(() => toDepartmentOptions(this.#departmentTree()));

  showDepartmentFilter = computed(() => this.#departmentOptions().length > 1);

  /** Pending absences and exchanges, together under each department. */
  pendingSections = computed(() => {
    const absences = this.pendingAbsenceRequests();

    return toPendingDepartmentSections(
      absences,
      toApprovableDepartmentsByEmployee(absences, this.#absenceViewContext()),
      [...this.supervisorApprovalExchanges(), ...this.employeeApprovalExchanges()],
      this.#departmentOptions(),
      this.#selectedDepartmentIds(),
    );
  });
  /**
   * Per pending section's `items`, the lists that section renders. Built from the typed lists above,
   * so each child list keeps its identity until the rows change.
   */
  pendingSplits = computed(() => {
    const { groups, otherGroups, noDepartmentItems } = this.pendingSections();
    const absences = new Set<PendingItem>(this.pendingAbsenceRequests());
    const supervisorApproval = new Set<PendingItem>(this.supervisorApprovalExchanges());
    const employeeAcceptance = new Set<PendingItem>(this.employeeApprovalExchanges());
    const itemLists = [...groups, ...otherGroups].map((group) => group.items).concat([noDepartmentItems]);

    return new Map(
      itemLists.map((items): [PendingItem[], PendingSplit] => [
        items,
        {
          absences: items.filter((item): item is EnhancedAbsenceModel => absences.has(item)),
          supervisorApproval: items.filter((item): item is ExchangeWithScheduleModel => supervisorApproval.has(item)),
          employeeAcceptance: items.filter((item): item is ExchangeWithScheduleModel => employeeAcceptance.has(item)),
        },
      ]),
    );
  });

  approvedOpenEndedAbsenceSections = computed(() => {
    const absences = this.approvedOpenEndedAbsences();

    return toAbsenceDepartmentSections(
      absences,
      toApprovableDepartmentsByEmployee(absences, this.#absenceViewContext()),
      this.#departmentOptions(),
      this.#selectedDepartmentIds(),
    );
  });

  noRequests = computed(() =>
    [this.pendingSections(), this.approvedOpenEndedAbsenceSections()].every(
      (sections) => countSectionItems(sections) === 0 && sections.hiddenCount === 0,
    ),
  );

  planType = PlanType;
  ExchangeRequestContext = ExchangeRequestContext;

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconClipboardRegular, mgcIconWarningRegular]);

    this.#panel.loadNextPage();
  }

  retryInitialLoad(): void {
    this.#panel.loadNextPage();
    this.#elementRef.nativeElement.focus();
  }
}
